import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider } from './hooks/useAuth';
import { ConfirmProvider } from './hooks/useConfirm';
import { ClinicAgentRoot } from './contexts/ClinicAgentRoot';
import { SidePanelProvider } from './contexts/SidePanelContext';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import AppShell from './components/AppShell';
import PageSkeleton from './components/PageSkeleton';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Login from './Login';

// Routes load on demand. The whole app used to arrive in one 995 kB chunk, so
// opening the login screen also downloaded the agent chat, recharts and every
// catalog editor.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PatientsList = lazy(() => import('./pages/PatientsList'));
const PatientDetail = lazy(() => import('./pages/PatientDetail'));
const InvitesList = lazy(() => import('./pages/InvitesList'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const SissiScoreGuide = lazy(() => import('./pages/SissiScoreGuide'));
const InsightsGuide = lazy(() => import('./pages/InsightsGuide'));
const InsightsOverview = lazy(() => import('./pages/InsightsOverview'));
const MetricsPage = lazy(() => import('./pages/MetricsPage'));
const ClinicsPage = lazy(() => import('./pages/ClinicsPage'));
const ClinicDetail = lazy(() => import('./pages/ClinicDetail'));
const AgentRunsPage = lazy(() => import('./pages/AgentRunsPage'));
const AgentRunDetail = lazy(() => import('./pages/AgentRunDetail'));
const LabPanelReviewPage = lazy(() => import('./pages/LabPanelReviewPage'));
const LabInboxPage = lazy(() => import('./pages/LabInboxPage'));

// Dev-only preview of the design system.
//
// The import sits inside a branch on import.meta.env.DEV, which Vite replaces
// with the literal `false` in a production build, so the whole `import()` is
// dead code and the chunk is never emitted. Guarding only the <Route> is not
// enough: the dynamic import would still be reachable and Rollup would emit
// the chunk anyway.
const DesignKit = lazy(() =>
  import.meta.env.DEV
    ? import('./pages/DesignKit')
    : Promise.resolve({ default: () => <></> }),
);

function App() {
  return (
    <AuthProvider>
      <TooltipProvider delayDuration={300}>
        <ConfirmProvider>
            <SidePanelProvider>
              <Router>
                <RouteErrorBoundary>
                  <Suspense fallback={<PageSkeleton />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    {/* The agent provider hydrates the transcript and opens the
                     *  socket on mount, so it belongs to the authenticated
                     *  shell rather than the app root. Mounted at the root it
                     *  also ran on /login — five paginated /api/chat/ requests
                     *  and a WebSocket, for a panel that screen never renders.
                     *  Its only consumer is ClinicAgentPanel, inside AppShell. */}
                    <Route
                      path="/app"
                      element={
                        <ClinicAgentRoot>
                          <AppShell />
                        </ClinicAgentRoot>
                      }
                    >
                      <Route index element={<Dashboard />} />
                      <Route path="patients" element={<PatientsList />} />
                      <Route path="patients/:id" element={<PatientDetail />} />
                      <Route path="patients/:id/analiticas/:panelId" element={<LabPanelReviewPage />} />
                      <Route path="analiticas" element={<LabInboxPage />} />
                      <Route path="invites" element={<InvitesList />} />
                      <Route path="resources" element={<ResourcesPage />} />
                      <Route path="sissi-score" element={<SissiScoreGuide />} />
                      <Route path="insights" element={<InsightsOverview />} />
                      <Route path="insights-guia" element={<InsightsGuide />} />
                      <Route path="metrics" element={<MetricsPage />} />
                      <Route path="clinics" element={<ClinicsPage />} />
                      <Route path="clinics/:id" element={<ClinicDetail />} />
                      <Route path="agent-runs" element={<AgentRunsPage />} />
                      <Route path="agent-runs/:id" element={<AgentRunDetail />} />
                    </Route>
                    {import.meta.env.DEV && <Route path="/_kit" element={<DesignKit />} />}
                    <Route path="*" element={<Navigate to="/login" />} />
                  </Routes>
                  </Suspense>
                </RouteErrorBoundary>
              </Router>
              {/* Mounted outside the router so a toast survives navigation. */}
              <Toaster position="bottom-right" richColors closeButton />
            </SidePanelProvider>
        </ConfirmProvider>
      </TooltipProvider>
    </AuthProvider>
  );
}

export default App;
